/** @jsxImportSource @emotion/react */
import { useLocation, useNavigate } from "react-router";

import { TFunction } from "i18next";
import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import useResponse from "@/shared/hooks/useResponse";
import { useNavigationStore } from "@/stores/useNavigationStore";
import { useVehiclesTableStore } from "@/stores/useTableStore";
import { css } from "@emotion/react";
import Typography from "@mui/material/Typography";

import { useAuth } from "../../../../../../../context/Auth/AuthProvider";
import { ROUTES } from "../../../../../../../shared";
import TextLink from "../../../../../../TextLink/TextLink";
import { TEXT_LINK_SIZES } from "../../../../../../TextLink/types";
import { FIND_VEHICLE_DTO_TYPE } from "../../../../../types";
import useChangeSelectedCompany from "../../../../CompanySelector/hooks/useChangeSelectedCompany";

interface FoundResourceDetailsProps {
  resourceData: FIND_VEHICLE_DTO_TYPE;
  t: TFunction<"translation", undefined>;
  onCloseDialog: () => void;
  searchValue: string;
}

const FoundResourceDetails = ({
  resourceData,
  t,
  onCloseDialog,
  searchValue
}: FoundResourceDetailsProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getData } = useAxiosPrivate();
  const location = useLocation();
  const { handleResponse } = useResponse();

  const { changeSelectedCompany, getCompanyList } = useChangeSelectedCompany();

  const { fetchData, resetFiltersAndDetailsData } = useVehiclesTableStore();

  const generateDetailsSingleRow = (title: string, value: string) => (
    <div css={css({ display: "flex", marginBottom: "8px" })}>
      <Typography variant="body2Medium" sx={{ width: "136px" }}>
        {title}:
      </Typography>
      <Typography variant="body2Medium">{value}</Typography>
    </div>
  );

  const handleGoToVehicles = async () => {
    const pageLocation = ROUTES.Vehicles;

    const hasToChangeCompany =
      user?.selectedCompany?.id !== resourceData.companyId;

    resetFiltersAndDetailsData(searchValue);

    if (hasToChangeCompany) {
      try {
        useNavigationStore.getState().setTargetRoute(pageLocation);
        await changeSelectedCompany(
          resourceData.companyId,
          false,
          pageLocation
        );
        await getCompanyList();
      } finally {
        useNavigationStore.getState().setTargetRoute(null);
      }
    } else {
      navigate(pageLocation);
    }

    if (location.pathname === pageLocation) {
      await fetchData(getData, handleResponse);
    }

    onCloseDialog();
  };

  return (
    <div css={css({ marginTop: "14px" })} data-testid="found-resource-details">
      {generateDetailsSingleRow(t("Dialog##vrn"), resourceData.vehicleNumber)}
      {generateDetailsSingleRow(t("Dialog##company"), resourceData.companyName)}
      {generateDetailsSingleRow(
        t("Dialog##client company"),
        resourceData.clientCompanyName
      )}
      <TextLink
        size={TEXT_LINK_SIZES.Normal}
        onClick={handleGoToVehicles}
        customStyle={{ textDecoration: "none" }}
      >
        {t("Actions##go to vehicle")}
      </TextLink>
    </div>
  );
};

export default FoundResourceDetails;
